import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import ProductCard from '@/components/feature/ProductCard';
import { useProducts, useCategories } from '@/hooks/useApi';
import { apiProductToProduct } from '@/services/adapters';

const sortOptions = [
  { value: 'featured', label: 'الأكثر شيوعاً' },
  { value: 'price-asc', label: 'السعر: من الأقل' },
  { value: 'price-desc', label: 'السعر: من الأعلى' },
  { value: 'newest', label: 'الأحدث' },
];

export default function ShopPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlCategory = searchParams.get('category');
  const urlSearch = searchParams.get('search');
  const sortBy = searchParams.get('sort') || 'featured';

  const { data: categories } = useCategories();
  const { data: apiProducts, loading } = useProducts(
    urlCategory ? { category: urlCategory } : urlSearch ? { search: urlSearch } : {}
  );

  const selectedCategory = apiProducts?.category ?? null;
  const childCategories = apiProducts?.children ?? [];

  const setCategory = (cat: string) => {
    setSearchParams(cat ? { category: cat } : {});
  };

  const setSort = (sort: string) => {
    const params: Record<string, string> = {};
    if (urlCategory) params.category = urlCategory;
    if (urlSearch) params.search = urlSearch;
    if (sort !== 'featured') params.sort = sort;
    setSearchParams(params);
  };

  const mainCategories = categories
    .filter(c => c.active && !c.parent_id)
    .map(c => c.name);

  const isChildSelected = selectedCategory && selectedCategory.parent_id;

  const products = useMemo(() => {
    if (!apiProducts?.data) return [];
    let result = apiProducts.data.map(p => apiProductToProduct(p));
    switch (sortBy) {
      case 'price-asc': result.sort((a, b) => a.price - b.price); break;
      case 'price-desc': result.sort((a, b) => b.price - a.price); break;
      case 'newest': result.reverse(); break;
      default: break;
    }
    return result;
  }, [apiProducts, sortBy]);

  return (
    <main className="min-h-screen">
      <div className="bg-cream-200 py-12 md:py-16">
        <div className="container-main text-center">
          <h1 className="heading-xl mb-3">{urlCategory || 'المتجر'}</h1>
          <p className="body-text">اكتشفي مجموعة منتجاتنا المختارة للعناية بالبشرة والجمال</p>
        </div>
      </div>

      <div className="sticky top-16 md:top-20 z-40 bg-cream-50/95 backdrop-blur-md border-b border-cream-300">
        <div className="container-main py-4">
          <div className="flex flex-col gap-3">
            {/* Main categories + Sort on same row */}
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2 overflow-x-auto pb-1 md:pb-0 scrollbar-hide flex-1 min-w-0">
                <button
                  onClick={() => setCategory('')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap flex-shrink-0 ${
                    !urlCategory ? 'bg-charcoal-900 text-white' : 'bg-cream-200 text-charcoal-600 hover:bg-cream-300'
                  }`}
                >
                  الكل
                </button>
                {mainCategories.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setCategory(cat)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap flex-shrink-0 ${
                      urlCategory === cat || (selectedCategory?.parent?.name === cat && !isChildSelected)
                        ? 'bg-charcoal-900 text-white'
                        : 'bg-cream-200 text-charcoal-600 hover:bg-cream-300'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-sm text-charcoal-500 whitespace-nowrap">ترتيب حسب:</span>
                <select
                  value={sortBy}
                  onChange={e => setSort(e.target.value)}
                  className="bg-white border border-cream-300 rounded-lg px-3 py-2 text-sm text-charcoal-900 focus:outline-none focus:border-blush-200"
                >
                  {sortOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Child categories row - shown when a parent is selected */}
            {childCategories.length > 0 && (
              <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide border-t border-cream-300 pt-3">
                {childCategories.map(child => (
                  <button
                    key={child.id}
                    onClick={() => setCategory(child.name)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap flex-shrink-0 ${
                      urlCategory === child.name
                        ? 'bg-blush-200 text-charcoal-900'
                        : 'bg-cream-100 text-charcoal-500 hover:bg-cream-300 hover:text-charcoal-700'
                    }`}
                  >
                    {child.name}
                  </button>
                ))}
              </div>
            )}

          </div>
        </div>
      </div>

      <section className="section-padding container-main">
        {loading ? (
          <div className="text-center py-20">
            <div className="animate-pulse w-16 h-16 bg-cream-200 rounded-full mx-auto mb-4" />
            <p className="text-charcoal-400">جاري التحميل...</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-charcoal-500 mb-6">{products.length} منتج</p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {products.map(product => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
            {products.length === 0 && (
              <div className="text-center py-20">
                <div className="w-20 h-20 rounded-full bg-cream-200 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-search-line text-3xl text-charcoal-400" />
                </div>
                <p className="text-charcoal-600 font-medium">لا توجد منتجات في هذا القسم</p>
              </div>
            )}
          </>
        )}
      </section>
    </main>
  );
}
